import { Link } from "react-router-dom";
import { AlertCircle, FileQuestion, RotateCcw } from "lucide-react";
import { Page } from "@/components/layout/page";
import { EmptyState } from "@/components/empty-state";
import { Button } from "@/components/ui/button";

interface EntityNotFoundProps {
  title: string;
  entityName?: string;
  description?: string;
  backLink?: { to: string; label: string };
  maxWidth?: string;
  embedded?: boolean;
}

export function EntityNotFound({
  title,
  entityName = "item",
  description = `This ${entityName} doesn't exist, was deleted, or the link is invalid.`,
  backLink,
  maxWidth = "max-w-2xl",
  embedded = false,
}: EntityNotFoundProps) {
  const content = (
    <EmptyState
      icon={FileQuestion}
      title={title}
      description={description}
      actions={
        backLink ? (
          <Button asChild>
            <Link to={backLink.to}>{backLink.label}</Link>
          </Button>
        ) : undefined
      }
    />
  );

  if (embedded) return content;

  return (
    <Page title={title} maxWidth={maxWidth} back={backLink}>
      {content}
    </Page>
  );
}

interface EntityErrorStateProps {
  title: string;
  description?: string;
  backLink?: { to: string; label: string };
  onRetry?: () => void;
  maxWidth?: string;
  embedded?: boolean;
}

export function EntityErrorState({
  title,
  description = "Something went wrong while loading this page. Please try again.",
  backLink,
  onRetry,
  maxWidth = "max-w-2xl",
  embedded = false,
}: EntityErrorStateProps) {
  const content = (
    <EmptyState
      icon={AlertCircle}
      title={title}
      description={description}
      actions={
        <div className="flex items-center gap-2">
          {onRetry && (
            <Button onClick={onRetry} variant="outline" className="gap-2">
              <RotateCcw className="h-4 w-4" />
              Try again
            </Button>
          )}
          {backLink && (
            <Button asChild>
              <Link to={backLink.to}>{backLink.label}</Link>
            </Button>
          )}
        </div>
      }
    />
  );

  if (embedded) return content;

  return (
    <Page title={title} maxWidth={maxWidth} back={backLink}>
      {content}
    </Page>
  );
}
